import React from 'react';
import { DashboardFilters } from '@interfaces/dashboard';
import { SalesCharts } from './SalesCharts';
import { GroupMetricsCharts } from './GroupMetricsCharts';
import { Paper } from '@mui/material';
import { Typography } from '@vibTheme/components/_base/Typography';
import styles from '@styles/Card.module.css';
import { SalesHistoryChart } from './SalesHistoryChart';
import { PaymentConversionSummary } from './PaymentConversion/PaymentConversionSummary';
import { RecurringRevenueSummary } from './RecurringRevenue/RecurringRevenueSummary';
import { BotFunnelSummary } from './BotFunnel/BotFunnelSummary';
import { userRolesDoPedido } from '@utils/userRolesDoPedido';

type Props = {
  dashBoardFilters: DashboardFilters;
};

export function DashboardCard(props: Props) {
  const {
    botConfigIds,
    startDate,
    endDate,
    previousPeriodStartDate,
    previousPeriodEndDate,
    clientTz,
    userRole,
  } = props.dashBoardFilters;

  return (
    <section>
      <SalesCharts
        filters={{
          botConfigIds,
          startDate,
          endDate,
          previousPeriodStartDate,
          previousPeriodEndDate,
          clientTz,
          userRoles: userRolesDoPedido(userRole),
        }}
      />

      {/*
        Hidden from affiliates, and the API rejects the role outright. An affiliate's claim on an invoice
        lives in its commission row, and commissions are only created once the invoice is PAID - so the
        denominator this block is built on does not exist for them, and scoping by community instead would
        expose the whole community's charges.
      */}
      {userRole !== 'AFFILIATED' && (
        <PaymentConversionSummary
          filters={{
            botConfigIds,
            startDate,
            endDate,
            previousPeriodStartDate,
            previousPeriodEndDate,
            clientTz,
            userRoles: userRolesDoPedido(userRole),
            periodRange: props.dashBoardFilters.periodRange,
          }}
        />
      )}

      {/*
        Same audience as the conversion block above, and the API rejects anything else. An affiliate earns
        on the invoices they brought, not on a subscriber base, and there is no honest way to say which
        subscriptions stay theirs across renewals - so a churn rate shown to them would be wrong in both
        directions.
      */}
      {userRole !== 'AFFILIATED' && (
        <RecurringRevenueSummary
          filters={{
            botConfigIds,
            startDate,
            endDate,
            clientTz,
            userRoles: userRolesDoPedido(userRole),
            periodRange: props.dashBoardFilters.periodRange,
          }}
        />
      )}

      <Paper className={styles.gridHistory}>
        <Typography component="h3" className="p-main">
          Histórico de Vendas
        </Typography>
        <SalesHistoryChart
          filters={{
            botConfigIds,
            clientTz,
            userRoles: userRolesDoPedido(userRole),
          }}
        />
      </Paper>
      {/* Respiro aqui, e não no componente: a dashboard nova o usa como último bloco, sem esse espaço. */}
      {userRole !== 'AFFILIATED' && (
        <div className="pb-main">
          <GroupMetricsCharts
            filters={{
              botConfigIds,
              startDate,
              endDate,
            }}
          />
        </div>
      )}

      {/*
        Hidden from affiliates, the same rule that already hides GroupMetricsCharts: the funnel exposes
        the community's traffic volume and how well it converts. The backend enforces this too - hiding
        it here is presentation, not authorization.
      */}
      {userRole !== 'AFFILIATED' && (
        <BotFunnelSummary
          filters={{
            botConfigIds,
            startDate,
            endDate,
            clientTz,
            periodRange: props.dashBoardFilters.periodRange,
            userRole,
          }}
        />
      )}
    </section>
  );
}
